import { useState } from 'react';
import {
  overrideOutstanding,
  releaseOutstanding,
  settleOutstanding,
} from '@/services/api/outstandings';
import { showErrorToast, showSuccessToast } from '@/components/toast/globalToast';
import { extractApiErrorMessage } from '@/utils';
import { DriverOutstanding } from '@/types/outstanding';

export type OutstandingModalState =
  | { type: 'settle' | 'release' | 'override'; row: DriverOutstanding }
  | null;

/**
 * Settle/release/override modal state + submit handlers, shared by the list
 * and detail pages so the two don't each carry their own copy of the same
 * open/submit/close plumbing. Each mutation returns the updated row, which
 * the caller patches into its own state via `onUpdated` — no refetch needed.
 */
export function useOutstandingActions(
  onUpdated: (row: DriverOutstanding) => void
) {
  const [modal, setModal] = useState<OutstandingModalState>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const closeModal = () => setModal(null);

  const run = async (label: string, action: () => Promise<DriverOutstanding>) => {
    setIsSubmitting(true);
    try {
      const updated = await action();
      showSuccessToast('Success', label);
      closeModal();
      onUpdated(updated);
    } catch (error) {
      showErrorToast('Error', extractApiErrorMessage(error, 'Action failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    modal,
    openModal: setModal,
    closeModal,
    isSubmitting,
    handleSettle: (amount: number, notes: string) => {
      if (!modal) return;
      run('Outstanding settled', () =>
        settleOutstanding(modal.row.id, { amount, notes })
      );
    },
    handleRelease: (reason: string) => {
      if (!modal) return;
      run('Outstanding released', () =>
        releaseOutstanding(modal.row.id, { releaseReason: reason })
      );
    },
    handleOverride: (reason: string) => {
      if (!modal) return;
      run('Override recorded', () =>
        overrideOutstanding(modal.row.id, { overrideReason: reason })
      );
    },
  };
}
